import { Component } from '@theme/component';

/**
 * Product copy block: collapse to one line behind "Read more" only when there
 * is something to collapse. See snippets/forcast-product-copy.liquid.
 *
 * The text is server-rendered in full. The collapsed state is CSS
 * (assets/forcast-product-copy.css): the first paragraph clamped to one line,
 * the rest hidden. This only decides whether that would hide anything - a
 * second paragraph, or a first one that runs past a line - and, if so, wires
 * the toggle. No JS, or a single short sentence, and the block simply shows
 * everything.
 *
 * A Component, not a run-once script, because a colour change morphs <main>
 * to the sibling product's HTML: the morph strips the collapse classes and
 * restores the toggle's `hidden`, then calls updatedCallback (or
 * connectedCallback when it recreates the element), and the new copy is
 * collapsed again from the top.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} inner - The copy itself.
 * @property {HTMLButtonElement} toggle - "Read more" / "Read less".
 *
 * @extends Component<Refs>
 */
class ForcastProductCopy extends Component {
  requiredRefs = ['inner', 'toggle'];

  connectedCallback() {
    super.connectedCallback();
    // Delegated, so it survives the morph replacing the button node.
    this.addEventListener('click', this.#toggleClicked);
    this.#collapse();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#collapse();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#toggleClicked);
  }

  #collapse() {
    const { inner, toggle } = this.refs;
    this.classList.remove('forcast-product-copy--open');
    this.classList.add('forcast-product-copy--collapsible');
    const first = inner.firstElementChild;
    // A couple of pixels of slack: fonts can still be settling, and a line
    // that fits exactly should not earn a toggle that reveals nothing.
    const clamped = inner.children.length > 1 || (first != null && first.scrollHeight > first.clientHeight + 2);
    if (!clamped) {
      this.classList.remove('forcast-product-copy--collapsible');
      toggle.hidden = true;
      return;
    }
    toggle.hidden = false;
    toggle.setAttribute('aria-expanded', 'false');
    toggle.textContent = toggle.dataset.labelMore ?? 'Read more';
  }

  /** @param {Event} event */
  #toggleClicked = (event) => {
    const { toggle } = this.refs;
    if (!(event.target instanceof Node) || !toggle.contains(event.target)) return;
    const open = this.classList.toggle('forcast-product-copy--open');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    toggle.textContent = open ? toggle.dataset.labelLess ?? 'Read less' : toggle.dataset.labelMore ?? 'Read more';
    if (!open) this.scrollIntoView({ block: 'nearest', behavior: 'instant' });
  };
}

if (!customElements.get('forcast-product-copy')) {
  customElements.define('forcast-product-copy', ForcastProductCopy);
}
